/**
 * Defensive guard: refuse to construct a wire request against an FE-
 * synthesized optimistic goalId. Called at the entry of every goal-id-
 * targeted mutation (`updateGoal`, `requestCancelGoal`, `requestHoldGoal`,
 * `requestReleaseGoal`) and from `buildUpdateGoalRequest`.
 *
 * In steady state this never fires — temp goals are removed from the
 * slice before any action handler can target them. The throw is a
 * safety net for future refactors that accidentally bypass the
 * optimistic-clear lifecycle, surfacing the bug loudly in the dev
 * console instead of letting the engine reject with an opaque error.
 *
 * Spec: `.claude/specs/optimistic-temp-remove-seed/` Req 5.
 */

/**
 * Both prefixes are recognised so the guard stays in lockstep with the
 * `isTempGoal` predicate (which feeds the action-menu / drag-gate). The
 * tilde-prefixed `~temp:` is the canonical form emitted by the
 * optimistic-temp-remove seed; the bare `temp:` is a legacy form that
 * lingers in older fixtures and any future code path that synthesizes
 * an optimistic id without the tilde. Keeping both in one constant
 * (consumed by `isTempGoal`) avoids drift where the action gate would
 * mark a `temp:`-prefixed goal as editable while this guard rejects
 * the resulting wire call with a developer error.
 */
export const TEMP_GOAL_ID_PREFIXES = ["~temp:", "temp:"] as const;

export const assertNotTempGoalId = (id: string, opName: string): void => {
  if (TEMP_GOAL_ID_PREFIXES.some((p) => id.startsWith(p))) {
    throw new Error(
      `${opName}: refusing to send temp goalId "${id}" to engine. ` +
        `This indicates an action handler fired against a synthetic optimistic goal — ` +
        `usually means the optimistic-clear effect didn't run before the action.`
    );
  }
};
